import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { cn } from '@/lib/utils';
import { LayoutDashboard, Package, ShoppingCart, FolderTree, Settings, Receipt, ClipboardList, UserCog, ChevronLeft, ChevronRight, LogOut, Menu, X, Bell, Search, Box, Building } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

function AdminLayout(props) {
  const { children, title, subtitle } = props;
  const { user, logout } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = function() {
    logout();
    navigate('/');
  };

  const closeMobile = function() {
    setMobileOpen(false);
  };

  const userName = user ? (user.name || 'Admin') : 'Admin';
  const userEmail = user ? (user.email || '') : '';
  const userInitial = userName.charAt(0);

  const menuItems = [
    { section: 'Main', items: [{ icon: LayoutDashboard, label: 'Dashboard', path: '/admin', exact: true }] },
    { section: 'Shop Management', items: [{ icon: ShoppingCart, label: 'Orders', path: '/admin/orders' }, { icon: Package, label: 'Products', path: '/admin/products' }, { icon: FolderTree, label: 'Categories', path: '/admin/categories' }] },
    { section: 'ERP / Finance', items: [{ icon: Receipt, label: 'Invoices', path: '/admin/erp/invoices' }, { icon: ClipboardList, label: 'Reports', path: '/admin/erp/reports' }, { icon: UserCog, label: 'Customers', path: '/admin/erp/customers' }] },
    { section: 'Settings', items: [{ icon: Settings, label: 'General', path: '/admin/settings' }, { icon: Building, label: 'ERP Settings', path: '/admin/erp/settings' }] }
  ];

  const isActive = function(path, exact) {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const renderMenuItem = function(item) {
    const active = isActive(item.path, item.exact);
    const Icon = item.icon;
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={closeMobile}
        className={cn(
          'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all',
          active ? 'bg-gold/20 text-gold border-l-4 border-gold' : 'text-gray-300 hover:bg-white/10',
          collapsed && 'justify-center'
        )}
      >
        <Icon className="h-5 w-5" />
        {!collapsed && <span>{item.label}</span>}
      </Link>
    );
  };

  const renderSection = function(section, idx) {
    return (
      <div key={idx} className="mb-6">
        {!collapsed && <h3 className="px-3 mb-2 text-xs text-gray-500 uppercase">{section.section}</h3>}
        <div className="space-y-1">{section.items.map(renderMenuItem)}</div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Desktop Sidebar */}
      <aside className={cn('hidden lg:block fixed left-0 top-0 bottom-0 bg-navy z-40 shadow-xl transition-all', collapsed ? 'w-20' : 'w-64')}>
        <div className="flex flex-col h-full">
          <div className={cn('flex items-center gap-3 px-4 py-4 border-b border-navy-light/20', collapsed && 'justify-center px-2')}>
            {collapsed ? (
              <img src="/logo.png" alt="GPGT" className="h-10 w-10 object-contain" />
            ) : (
              <img src="/logo.png" alt="Grand Palace General Trading" className="h-12 w-auto object-contain" />
            )}
          </div>
          <nav className="flex-1 overflow-y-auto py-4 px-3">{menuItems.map(renderSection)}</nav>
          <div className={cn('border-t border-navy-light/20 p-4', collapsed && 'px-2')}>
            {!collapsed ? (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gold/20 rounded-full flex items-center justify-center text-gold font-semibold">{userInitial}</div>
                <div className="flex-1"><p className="text-sm text-white truncate">{userName}</p><p className="text-xs text-gray-400 truncate">{userEmail}</p></div>
                <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white" onClick={handleLogout}><LogOut className="h-4 w-4" /></Button>
              </div>
            ) : (
              <Button variant="ghost" size="icon" className="w-full text-gray-400" onClick={handleLogout}><LogOut className="h-5 w-5" /></Button>
            )}
          </div>
          <button onClick={function() { setCollapsed(!collapsed); }} className="absolute -right-3 top-20 w-6 h-6 bg-navy border border-navy-light/30 rounded-full flex items-center justify-center text-gray-400 hover:text-white">
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={closeMobile} />}

      {/* Mobile Sidebar */}
      <aside className={cn('lg:hidden fixed left-0 top-0 bottom-0 w-64 bg-navy z-50 transform transition-transform shadow-xl', mobileOpen ? 'translate-x-0' : '-translate-x-full')}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-4 py-4 border-b border-navy-light/20">
            <img src="/logo.png" alt="Grand Palace" className="h-10 w-auto object-contain" />
            <button onClick={closeMobile} className="text-gray-400 hover:text-white"><X className="h-6 w-6" /></button>
          </div>
          <nav className="flex-1 overflow-y-auto py-4 px-3">{menuItems.map(renderSection)}</nav>
          <div className="border-t border-navy-light/20 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gold/20 rounded-full flex items-center justify-center text-gold font-semibold">{userInitial}</div>
              <div className="flex-1"><p className="text-sm text-white truncate">{userName}</p><p className="text-xs text-gray-400 truncate">{userEmail}</p></div>
              <Button variant="ghost" size="icon" className="text-gray-400" onClick={handleLogout}><LogOut className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={cn('flex-1 transition-all', collapsed ? 'lg:ml-20' : 'lg:ml-64')}>
        <header className="sticky top-0 z-30 bg-white border-b px-4 lg:px-6 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" className="lg:hidden" onClick={function() { setMobileOpen(true); }}><Menu className="h-6 w-6" /></Button>
              <div>
                {title && <h1 className="text-xl font-bold text-navy">{title}</h1>}
                {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden md:flex relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search..." className="pl-10 w-64 bg-gray-50" />
              </div>
              <Button variant="ghost" size="icon" className="relative"><Bell className="h-5 w-5" /><span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" /></Button>
              <Link to="/"><Button variant="outline" size="sm" className="hidden sm:flex"><Box className="h-4 w-4 mr-2" />Store</Button></Link>
            </div>
          </div>
        </header>
        <div className="p-4 lg:p-6">{children}</div>
      </main>
    </div>
  );
}

export default AdminLayout;
